import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { useTranslation } from "react-i18next";
import Helpers from "../../utils/helpers";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { useEffect, useState } from "react";
import { useAppDispatch } from "../../redux/hooks";
import { comment } from "../../redux/slices/comments";
import OrderService from "../../services/order/order.service";

const OrderTotal = (props: any) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState("");
  const dispatch = useAppDispatch();
  const [hasConfirmed, setHasConfirmed] = useState(false);

  // REWARDS STATE
  const [availableRewardPoints, setAvailableRewardPoints] = useState(0);
  const [rewardToCurrencyRatio, setRewardToCurrencyRatio] = useState(0);
  const [useRewards, setUseRewards] = useState(true);
  const [rewardsPointsRedeemed, setRewardsPointsRedeemed] = useState(0);

  // 1. BASE CALCULATION
  const rawOrderTotal =
    (props.totalItemCost || 0) +
    (props.totalTax || 0) +
    (props.courierCharges || 0) +
    (props.deliveryHandlingCharges || 0) +
    (props.packingCharges || 0) +
    (props.paymentGatewayCharges || 0);

  const orderTotalRounded = Math.round(rawOrderTotal);

  // 2. FETCH REWARDS DATA
  useEffect(() => {
    if (!props.userId) return;
    const fetchRewards = async () => {
      try {
        const res = await OrderService.getUserRewardBalance(props.userId);
        const points = res?.availablePoints ?? 0;
        const ratio = res?.rewardToCurrencyRatio ?? 0;
        setAvailableRewardPoints(points);
        setRewardToCurrencyRatio(ratio);

        // Update the parent state so OrderDetails knows the ratio
        if (props.setRewardRatio) {
          props.setRewardRatio(ratio);
        }
        if (points <= 0) setUseRewards(false);
      } catch (error) {
        console.error("Failed to fetch reward points", error);
        setUseRewards(false);
      }
    };
    fetchRewards();
  }, [props.userId]);

  // 3. REWARDS REDEMPTION LOGIC
  useEffect(() => {
    if (useRewards && rewardToCurrencyRatio > 0) {
      const pointsNeeded = Math.floor(orderTotalRounded / rewardToCurrencyRatio);
      setRewardsPointsRedeemed(Math.min(availableRewardPoints, pointsNeeded));
    } else {
      setRewardsPointsRedeemed(0);
    }
  }, [useRewards, availableRewardPoints, orderTotalRounded, rewardToCurrencyRatio]);

  const handleCommentChange = (e: any) => {
    setComments(e);
    dispatch(comment(e));
  };

  const rewardDiscountAmount = rewardsPointsRedeemed * rewardToCurrencyRatio;

  const calculateFinalTotal = () => {
    return Math.round(Math.max(orderTotalRounded - rewardDiscountAmount, 0));
  };

  return (
    <div className="w-full">
      <div className="grid grid-nogutter">
        <div className="col-12">
          <div className="p-card shadow-0 border-none bg-white border-top-3 border-primary overflow-hidden">
            <div className="grid grid-nogutter">

              {/* LEFT SECTION: Interaction */}
              <div className="col-12 md:col-6 p-4 md:p-5">
                <h3 className="text-900 font-bold mb-3 mt-0 text-xl">Order Instruction</h3>
                <div className="p-fluid">
                  <InputTextarea
                    placeholder="Add a note for the delivery partner..."
                    rows={4}
                    className="w-full border-300"
                    value={comments}
                    onChange={(e) => handleCommentChange(e.target.value)}
                    maxLength={250}
                    autoResize
                  />
                </div>
                <p className="text-500 text-sm mt-2 text-right">
                  {250 - comments.length} characters left
                </p>

                <Divider className="my-4" />

                {/* REWARDS INTERFACE */}
                <div
                  className="p-3 border-round-lg border-1"
                  style={{
                    background: useRewards ? '#f0fdf4' : '#fafafa',
                    borderColor: useRewards ? '#bbf7d0' : '#dee2e6'
                  }}
                >
                  <div className="flex align-items-center justify-content-between mb-3">
                    <div className="flex align-items-center gap-2">
                      <span className="text-xl">🎁</span>
                      <div>
                        <span className="font-bold text-800 text-sm uppercase tracking-wider block">Reward Points</span>
                        {rewardToCurrencyRatio > 0 && (
                          <span className="text-xs text-600 font-medium">
                            1 Point = {Helpers.formatAmountInINR(rewardToCurrencyRatio)}
                          </span>
                        )}
                      </div>
                    </div>
                    <InputSwitch
                      checked={useRewards}
                      onChange={(e) => setUseRewards(e.value ?? false)}
                      disabled={availableRewardPoints <= 0}
                    />
                  </div>

                  <div className="flex justify-content-between align-items-end">
                    <div>
                      <p className="m-0 text-600 text-xs font-bold uppercase">Available</p>
                      <p className="m-0 text-xl font-bold text-900">
                        {availableRewardPoints.toLocaleString()} <span className="text-xs font-medium text-500">pts</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="m-0 text-600 text-xs font-bold uppercase">Redeeming</p>
                      <p className={`m-0 text-xl font-bold ${useRewards ? 'text-green-600' : 'text-400'}`}>
                        {useRewards ? `${rewardsPointsRedeemed.toLocaleString()}` : '0'} <span className="text-xs font-medium text-500">pts</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT SECTION: Financial Breakdown */}
              <div className="col-12 md:col-6 p-4 md:p-5 bg-white flex flex-column justify-content-between">
                <div>
                  <h3 className="text-900 font-bold mb-4 mt-0 text-xl">Order Summary</h3>

                  <div className="flex flex-column gap-2">
                    <div className="flex justify-content-between text-700">
                      <span>Total Cost:</span>
                      <span className="font-semibold text-900">{Helpers.formatAmountInINR(props.totalItemCost)}</span>
                    </div>

                    <div className="flex justify-content-between text-700">
                      <span>Tax:</span>
                      <span className="font-medium text-900">{Helpers.formatAmountInINR(props.totalTax)}</span>
                    </div>

                    <div className="flex justify-content-between text-700">
                      <span>Delivery Handling Charges:</span>
                      <span className="font-medium text-900">{Helpers.formatAmountInINR(props.deliveryHandlingCharges)}</span>
                    </div>

                    <div className="flex justify-content-between text-700">
                      <span>Courier Charges:</span>
                      <span className="font-medium text-900">{Helpers.formatAmountInINR(props.courierCharges)}</span>
                    </div>

                    <div className="flex justify-content-between text-700">
                      <span>Packing Charges:</span>
                      <span className="font-medium text-900">{Helpers.formatAmountInINR(props.packingCharges)}</span>
                    </div>

                    <div className="flex justify-content-between text-700">
                      <span>Payment Getway Charges:</span>
                      <span className="font-medium text-900">{Helpers.formatAmountInINR(props.paymentGatewayCharges)}</span>
                    </div>

                    {/* {useRewards && rewardsPointsRedeemed > 0 && (
                      <div className="flex justify-content-between text-green-700 font-bold bg-green-50 p-2 border-round-lg mt-2 border-1 border-green-100">
                        <span className="text-sm">Rewards Applied</span>
                        <span className="font-bold">-{Helpers.formatAmountInINR(rewardDiscountAmount)}</span>
                      </div>
                    )} */}

                    <Divider className="my-2" />

                    <div className="flex justify-content-between align-items-center pt-2">
                      <div>
                        <span className="text-xl font-extrabold text-900 block">Total Payable</span>
                        {useRewards && rewardsPointsRedeemed > 0 && (
                          <span className="text-xs font-bold text-green-600 uppercase">
                            You saved {Helpers.formatAmountInINR(rewardDiscountAmount)}!
                          </span>
                        )}
                      </div>
                      <div className="text-right font-bold">
                        {useRewards && rewardsPointsRedeemed > 0 && (
                          <p className="m-0 text-x text-500 line-through">
                            {Helpers.formatAmountInINR(orderTotalRounded)}
                          </p>
                        )}
                        <p className="m-0 text-3xl font-black text-primary">
                          {Helpers.formatAmountInINR(calculateFinalTotal())}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-column gap-2 mt-4">
                  <Button
                    label={calculateFinalTotal() > 0 ? "Proceed to Payment" : "Place Order Now"}
                    icon={calculateFinalTotal() > 0 ? "pi pi-money-bill" : "pi pi-shopping-bag"}
                    className="p-button-primary p-py-3 font-bold border-round-lg w-full text-lg shadow-2"
                    onClick={() => { props.confirmOrder(rewardsPointsRedeemed, calculateFinalTotal()); }}
                    loading={props.isPlacingOrder}
                    disabled={props.isPlacingOrder}
                  />
                  <Button
                    label="Back to Cart"
                    icon="pi pi-arrow-left"
                    className="p-button-text p-button-secondary w-full text-sm font-semibold"
                    onClick={props.updateItem}
                  />
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTotal;